// Mirrors apps/api/modules/evidence/types/evidence.ts and the draft row minus the dossier snapshot.

export type EvidenceDraftStatus = 'queued' | 'collecting' | 'rendering' | 'generated' | 'failed' | 'superseded';

export type EvidenceOrderType = 'initial' | 'renewal';

// Mirrors EVIDENCE_PROVIDERS in apps/api/modules/evidence/types/evidence.ts.
export const EVIDENCE_PROVIDERS = ['shopify', 'paypal', 'stripe', 'adyen'] as const;
export type EvidenceProvider = typeof EVIDENCE_PROVIDERS[number];
// Mirrors DOCUMENT_PROVIDERS in apps/api/modules/evidence/types/evidence.ts.
export const DOCUMENT_PROVIDERS = [...EVIDENCE_PROVIDERS, 'bank'] as const;
export type DocumentProvider = typeof DOCUMENT_PROVIDERS[number];

// Mirrors EvidenceOrderSourceKind in apps/api/modules/evidence/types/evidence.ts.
export type EvidenceOrderSourceKind = 'shopify' | 'checkout_champ';

// Mirrors EXCLUDABLE_SECTIONS / FORCEABLE_SECTIONS / EXCLUDABLE_FACTS in apps/api/modules/evidence/types/evidence.ts.
export const EXCLUDABLE_SECTIONS = [
  'ipLocation', 'securedPayment', 'proofOfRefund', 'orderHistory', 'customerCommunication', 'fulfillment',
] as const;
export type ExcludableSection = typeof EXCLUDABLE_SECTIONS[number];
export const FORCEABLE_SECTIONS = ['ipLocation', 'orderHistory', 'checkoutProcess'] as const;
export type ForceableSection = typeof FORCEABLE_SECTIONS[number];
export type FactId = 'order' | 'disputedCharge' | 'noContact' | 'customerContact' | 'renewalAuth' | 'subscriptionCancelled' | 'transactionHistory' | 'verifiedChecks' | 'tracking' | 'digitalUsage' | 'emailActivity';

// Mirrors DOCUMENT_SECTIONS in apps/api/modules/evidence/types/evidence.ts: every section, in print order.
export const DOCUMENT_SECTIONS = [
  'productDescription', 'factsOfTheCase', 'orderHistory', 'customerCommunication', 'customerInformation',
  'securedPayment', 'orderReceipt', 'proofOfRefund', 'ipLocation', 'fulfillment', 'digitalUsage', 'checkoutProcess', 'policies',
] as const;
export type DocumentSection = typeof DOCUMENT_SECTIONS[number];

// Mirrors SECTION_SLOTS in apps/api/modules/evidence/types/evidence.ts: the slots that print inside each section.
export const SECTION_SLOTS: Record<DocumentSection, readonly EvidenceSlot[]> = {
  productDescription: ['product_photo'],
  factsOfTheCase: [],
  orderHistory: ['order_history'],
  customerCommunication: ['customer_communication'],
  customerInformation: [],
  securedPayment: ['order_status', 'payment_details'],
  orderReceipt: ['admin_order', 'email_confirmation'],
  proofOfRefund: ['proof_of_refund'],
  ipLocation: ['ip_location'],
  fulfillment: ['tracking_page', 'proof_of_delivery'],
  digitalUsage: ['account_activity', 'product_usage', 'email_activity'],
  checkoutProcess: ['order_page', 'checkout_process'],
  policies: [],
};

// Mirrors apps/api/modules/evidence/types/evidencePackage.ts.
export const PACKAGE_PROVIDERS = ['shopify', 'stripe', 'paypal', 'adyen'] as const;
export type PackageProvider = typeof PACKAGE_PROVIDERS[number];

export const SECTION_IDS = {
  productDescription: ['product-description'],
  factsOfTheCase: ['facts-of-the-case'],
  orderHistory: ['order-history'],
  customerCommunication: ['customer-communication'],
  customerInformation: ['customer-information'],
  securedPayment: ['secured-payment'],
  orderReceipt: ['order-receipt'],
  proofOfRefund: ['proof-of-refund'],
  ipLocation: ['ip-location'],
  fulfillment: ['tracking-information'],
  digitalUsage: ['digital-usage'],
  checkoutProcess: ['checkout-process'],
  policies: ['cancellation-policy', 'refund-policy', 'shipping-policy'],
} as const satisfies Record<DocumentSection, readonly string[]>;

type PolicyId = typeof SECTION_IDS.policies[number];

/** A whole section, or only some of the policies' rendered ids. */
export type PackageSectionSelector = DocumentSection | { section: 'policies'; ids: readonly PolicyId[] };

/**
 * A category file cuts its sections out; the whole file is the document as it printed, uploaded into the processor's `slot`;
 * the rest file holds every page no category file took, so the cuts and it add up to the document once.
 */
export type PackageFileSpec = { label: string; sections: readonly PackageSectionSelector[] } | { label: string; whole: true; slot: string } | { label: string; rest: true; slot: string };

export const PACKAGE_FILES = {
  shopify: {
    customer_communication: { label: 'Customer communication', sections: ['customerCommunication'] },
    shipping_documentation: { label: 'Shipping documentation', sections: ['fulfillment', { section: 'policies', ids: ['shipping-policy'] }] },
    service_documentation: { label: 'Proof of service', sections: ['orderReceipt', 'digitalUsage'] },
    cancellation_policy: { label: 'Cancellation policy', sections: [{ section: 'policies', ids: ['cancellation-policy'] }] },
    refund_policy: { label: 'Refund policy', sections: [{ section: 'policies', ids: ['refund-policy'] }] },
    uncategorized: { label: 'Whole document', whole: true, slot: 'Uncategorized file' },
  },
  stripe: {
    receipt: { label: 'Receipt', sections: ['orderReceipt', 'customerInformation', 'securedPayment'] },
    customer_communication: { label: 'Customer communication', sections: ['customerCommunication'] },
    shipping_documentation: { label: 'Shipping documentation', sections: ['fulfillment', { section: 'policies', ids: ['shipping-policy'] }] },
    cancellation_policy: { label: 'Cancellation policy', sections: [{ section: 'policies', ids: ['cancellation-policy'] }] },
    refund_policy: { label: 'Refund policy', sections: [{ section: 'policies', ids: ['refund-policy'] }] },
    service_documentation: { label: 'Service documentation', sections: ['digitalUsage'] },
    uncategorized: { label: 'Other sections', rest: true, slot: 'Uncategorized file' },
  },
  paypal: {
    proof_of_fulfillment: { label: 'Proof of fulfillment', sections: ['fulfillment'] },
    proof_of_refund: { label: 'Proof of refund', sections: ['proofOfRefund'] },
    return_policy: { label: 'Return or refund policy', sections: ['policies'] },
    other: { label: 'Whole document', whole: true, slot: 'Other supporting documents' },
  },
  adyen: {
    defense_material: { label: 'Whole document', whole: true, slot: 'Defense material' },
    tid_or_invoice: { label: 'Receipt', sections: ['orderReceipt', 'customerInformation', 'securedPayment'] },
    proof_of_goods_provided: { label: 'Proof of delivery', sections: ['fulfillment', 'digitalUsage'] },
    disclosure_at_poi: { label: 'Policies at checkout', sections: ['checkoutProcess', 'policies'] },
  },
} as const satisfies Record<PackageProvider, Record<string, PackageFileSpec>>;

export type PackageFileKey = { [P in PackageProvider]: keyof typeof PACKAGE_FILES[P] }[PackageProvider];

export interface EvidencePackageFile {
  file: PackageFileKey;
  label: string;
  /** The document sections this file holds; only those that printed. */
  sections: DocumentSection[];
  bytes: number;
  pages: number;
  /** The limit this file breaks, as the sentence the page shows; null when it fits. */
  overLimit: string | null;
  /** The whole document rather than a cut: it downloads the dossier itself. */
  whole?: true;
  /** The processor's upload slot the whole document goes into. */
  slot?: string;
}

export interface EvidencePackage {
  files: EvidencePackageFile[];
  total: { bytes: number; pages: number; overLimit: string | null };
}

/** Mirrors OutlineSection in apps/api/modules/evidence/types/evidence.ts. */
export interface OutlineSection {
  key: DocumentSection;
  /** Prints with nothing excluded. */
  prints: boolean;
  /** Ticked when the agent has said nothing. False only for a section the run left out or one the package does not normally carry. */
  defaultOn: boolean;
  /** This row's slots whose exhibit the document builds itself; an attachment there only replaces it. */
  generatedSlots: EvidenceSlot[];
  /** A rule left it out and the agent's tick can print it. */
  forceable: boolean;
}

/** Mirrors OutlineRefund in apps/api/modules/evidence/types/evidence.ts. */
export interface OutlineRefund {
  source: 'store' | 'manual' | 'none';
  expected: boolean;
  /** "<amount> refunded on <date> per <source>" for a store refund; null otherwise. */
  summary: string | null;
}

/** Mirrors DossierTemplateKind in apps/api/modules/evidence/dossier/template.ts. */
export type DossierTemplateKind = 'physical' | 'digital';

/** Mirrors EvidenceOutline in apps/api/modules/evidence/types/evidence.ts. */
export interface EvidenceOutline {
  template: DossierTemplateKind;
  /** In print order. */
  sections: OutlineSection[];
  refund: OutlineRefund;
  facts: { id: FactId; text: string }[];
  /** The storefront the document printed, the agent's pick now and at that generation, and the dispute's company's storefronts to pick from. */
  storefront: { printedId: number | null; printedName: string | null; chosenId: number | null; companyId: number | null; options: StorefrontOption[] };
}

/** Mirrors StorefrontOption in apps/api/modules/evidence/types/evidence.ts. */
export interface StorefrontOption {
  storefrontId: number;
  name: string;
  domain: string | null;
}

type ReasonFamily = 'fraudulent' | 'product' | 'subscription_canceled' | 'general';

export type EvidenceAttentionCode =
  | 'customer_comms_missing'
  | 'tracking_unavailable'
  | 'tracking_pending'
  | 'policy_missing'
  | 'cvc_avs_unavailable'
  | 'static_asset_missing'
  | 'brand_logo_missing'
  | 'order_unavailable'
  | 'admin_order_missing'
  | 'payment_details_missing'
  | 'email_confirmation_missing'
  | 'ip_location_capture'
  | 'order_page_capture'
  | 'order_status_capture'
  | 'order_history_missing'
  | 'ip_location_unavailable'
  | 'ip_location_mismatch'
  | 'ip_location_same_state'
  | 'ip_location_anonymous'
  | 'source_access_note'
  | 'order_history_unavailable'
  | 'enrolment_unavailable'
  | 'refund_pending'
  | 'capture_failed'
  | 'delivery_failed'
  | 'delivered_elsewhere'
  | 'conclusion_missing'
  | 'storefront_unresolved'
  | 'reason_unclassified'
  | 'disputed_parcels_untracked'
  | 'disputed_item_not_in_parcel'
  | 'parcel_never_moved'
  | 'disputed_items_unproven'
  | 'digital_proof_missing'
  | 'account_page_missing';

export type EvidenceSlot =
  | 'admin_order'
  | 'payment_details'
  | 'email_confirmation'
  | 'customer_communication'
  | 'order_history'
  | 'checkout_process'
  | 'order_page'
  | 'ip_location'
  | 'order_status'
  | 'proof_of_delivery'
  | 'tracking_page'
  | 'product_photo'
  | 'proof_of_refund'
  | 'account_activity'
  | 'product_usage'
  | 'email_activity';

/** Mirrors EVIDENCE_APPENDING_SLOTS in apps/api/modules/evidence/types/evidence.ts: a second file adds to these. */
export const EVIDENCE_APPENDING_SLOTS: readonly EvidenceSlot[] = [
  'proof_of_delivery', 'email_confirmation', 'customer_communication', 'proof_of_refund',
  'account_activity', 'product_usage', 'email_activity',
];

/** Mirrors EvidenceInputOverrides in apps/api/modules/evidence/types/evidence.ts. */
export type CustomerContactKind = 'after_renewal' | 'delivery_status' | 'other';

/** Mirrors CustomerContact in apps/api/modules/evidence/types/evidence.ts. */
export interface CustomerContact {
  kind: CustomerContactKind;
  note?: string;
}

/** Mirrors ManualRefund in apps/api/modules/evidence/types/evidence.ts. */
export interface ManualRefund {
  amount: string;
  refundedOn: string;
}

/** Mirrors DigitalDetails in apps/api/modules/evidence/types/evidence.ts. */
export interface DigitalDetails {
  accountCreatedOn?: string | null;
  lastLoginAt?: string | null;
  emailOpened?: boolean;
}

export interface EvidenceInputOverrides {
  attachments?: EvidenceAttachmentRef[];
  /** Keyed by product title: the agent's description prints instead of Shopify's. */
  productDescriptions?: Record<string, string>;
  customerContact?: CustomerContact | null;
  excludedSections?: ExcludableSection[];
  /** Sections a rule left out that the agent ticked back in. */
  includedSections?: ForceableSection[];
  excludedFacts?: FactId[];
  /** Read only when the receipt holds no settled refund. */
  manualRefund?: ManualRefund | null;
  /** The storefront the agent picked for an order that matched none. */
  storefrontId?: number | null;
  /** Read only by the digital template. */
  digitalDetails?: DigitalDetails;
  trackingNumber?: string | null;
  shopifyAnswers?: Partial<Record<ShopifyAnswerField, string>>;
  paypalNotes?: Record<string, string>;
  stripeAnswers?: Partial<Record<StripeAnswerField, string>>;
  adyenDefense?: { reasonCode: string } | null;
}

// Mirrors SHOPIFY_ANSWER_FIELDS in apps/api/modules/evidence/types/evidence.ts.
export const SHOPIFY_ANSWER_FIELDS = ['uncategorizedText', 'refundPolicyDisclosure', 'cancellationPolicyDisclosure'] as const;
export type ShopifyAnswerField = typeof SHOPIFY_ANSWER_FIELDS[number];
// Mirrors STRIPE_ANSWER_FIELDS in apps/api/modules/evidence/types/evidence.ts.
export const STRIPE_ANSWER_FIELDS = ['uncategorizedText', 'productDescription', 'refundPolicyDisclosure', 'cancellationPolicyDisclosure', 'cancellationRebuttal', 'refundRefusalExplanation', 'duplicateChargeExplanation'] as const;
export type StripeAnswerField = typeof STRIPE_ANSWER_FIELDS[number];
/** Mirrors STRIPE_FIXED_FIELDS in the API, with the column's label for each field the hash sends without asking. */
export const STRIPE_FIXED_LABELS = {
  customer_name: 'Customer name',
  customer_email_address: 'Customer email',
  customer_purchase_ip: 'Purchase IP',
  billing_address: 'Billing address',
  shipping_address: 'Shipping address',
  shipping_carrier: 'Shipping carrier',
  shipping_tracking_number: 'Tracking number',
  shipping_date: 'Shipping date',
} as const;
export type StripeFixedField = keyof typeof STRIPE_FIXED_LABELS;
/** Mirrors FILE_FIELDS in apps/api/modules/evidence/submit/stripeEvidence.ts, as the column prints each field. */
export const STRIPE_FILE_FIELD_LABELS: Record<keyof typeof PACKAGE_FILES['stripe'], string> = {
  receipt: 'Receipt',
  customer_communication: 'Customer communication',
  shipping_documentation: 'Shipping documentation',
  cancellation_policy: 'Cancellation policy',
  refund_policy: 'Refund policy',
  service_documentation: 'Service documentation',
  uncategorized: 'Uncategorized file',
};

/** Processors the page can submit to; mirrors the dispatch in apps/api/modules/evidence/submit/evidenceSubmissionService.ts. */
export const SUBMISSION_PROVIDERS = ['shopify', 'paypal', 'adyen', 'stripe'] as const;
export type SubmissionProvider = typeof SUBMISSION_PROVIDERS[number];
/** Mirrors PAYPAL_NOTE_KEY in the API: the one PayPal note's key in a submission view's answers. */
export const PAYPAL_NOTE_KEY = "NOTE";

/** Mirrors SubmissionAnswer in apps/api/modules/evidence/types/evidence.ts. */
export interface SubmissionAnswer {
  label: string;
  text: string;
  source: string;
  edited: boolean;
}

/** Mirrors SubmissionPlanFile in apps/api/modules/evidence/types/evidencePackage.ts. */
export interface SubmissionPlanFile {
  file: PackageFileKey;
  label: string;
  bytes: number;
  pages: number;
  send: boolean;
  /** Why the file stays home; null when it goes. */
  skipReason: string | null;
}

/** Mirrors ShopifySubmissionView in apps/api/modules/evidence/types/evidence.ts. */
export interface ShopifySubmissionView {
  provider: 'shopify';
  files: SubmissionPlanFile[];
  totalBytes: number;
  answers: Partial<Record<ShopifyAnswerField, SubmissionAnswer>>;
  fixed: {
    customerFirstName: string | null;
    customerLastName: string | null;
    customerEmail: string | null;
    shippingAddress: { address1: string | null; address2: string | null; city: string | null; province: string | null; zip: string | null; country: string | null } | null;
  };
}

/** Mirrors PayPalSubmissionView: one note under PAYPAL_NOTE_KEY, what PayPal asked for, and the one type the response goes as. */
export interface PayPalSubmissionView {
  provider: 'paypal';
  files: SubmissionPlanFile[];
  totalBytes: number;
  answers: Record<string, SubmissionAnswer>;
  tracking: { carrier: string | null; number: string; url: string | null }[];
  requested: { type: string; label: string }[];
  entry: { type: string; label: string };
  /** Whether PayPal asks for a return address, and the printed storefront's one that would go; null when it has none. */
  returnAddress: { requested: boolean; address: ReturnAddress | null };
}

/** Mirrors AdyenSubmissionView: Adyen's reasons against our files, the one selected, or why none could be read. */
export interface AdyenDefenseDocument { type: string; label: string; requirement: 'Required' | 'OneOrMore' | 'Optional' | 'AlternativeRequired'; file: PackageFileKey | null }
export interface AdyenDefenseReason { code: string; label: string; satisfiable: boolean; documents: AdyenDefenseDocument[]; missing: string[] }
export interface AdyenSubmissionView {
  provider: 'adyen';
  files: SubmissionPlanFile[];
  totalBytes: number;
  answers: Record<string, SubmissionAnswer>;
  reasons: AdyenDefenseReason[];
  selected: string | null;
  reasonsError: string | null;
}

/** Mirrors StripeSubmissionView in apps/api/modules/evidence/types/evidence.ts; `canSubmit` is the key's probe verdict, re-run on this read when it was null or false. */
export interface StripeSubmissionView {
  provider: 'stripe';
  canSubmit: boolean | null;
  files: SubmissionPlanFile[];
  totalBytes: number;
  totalPages: number;
  /** Pages the text answers add, judged with the files against Stripe's 50. */
  textPages: number;
  overLimit: string | null;
  answers: Partial<Record<StripeAnswerField, SubmissionAnswer>>;
  fixed: Partial<Record<StripeFixedField, string>>;
}

export type EvidenceSubmissionView = ShopifySubmissionView | PayPalSubmissionView | AdyenSubmissionView | StripeSubmissionView;

/** Mirrors PayPalCaseView in apps/api/types/disputeEvidenceSubmission.ts. */
export interface PayPalCaseView {
  reopened: { at: string; reason: string | null; reason_changed: boolean; requested: string[] } | null;
  stale_pack: boolean;
  rounds: number;
}

/** Mirrors DisputeEvidenceSubmissionView in apps/api/types/disputeEvidenceSubmission.ts. */
export interface DisputeEvidenceSubmission {
  submissionId: number;
  status: 'sending' | 'saved' | 'sent' | 'failed';
  sentAt: string | null;
  updatedAt: string;
  userName: string | null;
  files: { file: string; label: string; bytes: number; pages: number }[];
  answersEdited: boolean;
  errorMessage: string | null;
  /** A saved response the document or the answers moved past; another save is due. */
  stale: boolean;
}

/** Mirrors EvidenceGeneratedProduct in apps/api/modules/evidence/types/evidence.ts. */
export interface EvidenceGeneratedProduct {
  title: string;
  /** What the document printed. */
  productDescription: string | null;
  /** Shopify's text when the agent's printed instead; null when Shopify's did. */
  shopifyDescription: string | null;
  /** The store's own product image; a funnel order carries none. */
  imageUrl: string | null;
}

/** Mirrors MAX_PRODUCT_DESCRIPTION_CHARS in apps/api/modules/evidence/types/evidence.ts. */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 2000;

export interface EvidenceAttachmentRef {
  slot: EvidenceSlot;
  storageKey: string;
  fileName: string;
  contentType: 'image/png' | 'image/jpeg';
  /** product_photo only: the product the photo prints beside, one photo per product. */
  productTitle?: string;
}

export interface EvidenceAttentionItem {
  code: EvidenceAttentionCode;
  message: string;
  severity: 'required' | 'recommended';
  attachableSlot?: EvidenceSlot;
  /** The document already prints a rendering of this section; an attachment only replaces it. */
  generated?: boolean;
  /** Where the gap is closed: the company's storefront settings, opened on this storefront when one resolved. */
  storefrontSetup?: { companyId: number; storefrontId: number | null };
}

export interface EvidenceConclusion {
  paragraphs: string[];
  source: 'model' | 'agent';
  /** The clauses paragraph 3 was assembled from; absent on an agent edit. */
  argument?: { clauseIds: string[] };
  writtenAt: string;
}

// Mirrors ConclusionFacts in apps/api/modules/evidence/types/evidence.ts.
export interface ConclusionFacts {
  merchantName?: string;
  orderNumber?: string;
  orderDate?: string;
  enrolledOn?: string;
  renewedOn?: string[];
  renewalCount?: number;
  amount?: string;
  disputedItems?: string[];
  customerName?: string;
  provider: DocumentProvider;
  bankName?: string;
  reasonFamily: ReasonFamily;
  /** Only a renewal can have breached subscription terms, whatever the policy pages disclose. */
  orderType: EvidenceOrderType;
  subscription?: true;
  reasonCode?: string;
  reasonLabel?: string;
  networkLine?: string;
  shippingAddress?: string;
  verifiedChecks?: ('CVV' | 'AVS')[];
  paymentReference?: string;
  ip?: { address: string; matchesBilling: 'city' | 'postal code' | 'region' | 'country' };
  tracking?: {
    parcelUndelivered?: true;
    carrier?: string;
    number: string;
    shippedOn?: string;
    deliveredOn?: string;
    deliveredTo?: 'destination city' | 'destination postal code' | 'destination city and postal code';
    signedBy?: string;
  };
  noContactAttested?: true;
  /** The order history prints more than this one order, so it can be called a history. */
  priorOrders?: true;
  /** The renewal-authorization bullet is printed, so the renewal argument may be made. */
  renewalAuthorized?: true;
  policiesShown?: ('cancellation' | 'refund' | 'shipping')[];
  /** A non-receipt dispute whose carrier shows the disputed parcels never moved: nothing may be concluded. */
  shipmentUnproven?: true;
  digital?: boolean;
  digitalProof?: {
    accountActivity: boolean;
    productUsage: boolean;
    emailActivity: boolean;
    emailOpened: boolean;
    accountCreatedOn?: string;
    lastLoginOn?: string;
    cancelledOn?: string;
  };
  sectionsPrinted: string[];
}

// Mirrors ConclusionFactsView in apps/api/modules/evidence/types/evidence.ts.
export interface ConclusionFactsView {
  facts: ConclusionFacts;
  /** The fact run and the argument with their bold as `**` markers; null without a generated conclusion. */
  editable: [string, string] | null;
}

/** Mirrors MAX_CONCLUSION_PARAGRAPH_CHARS in apps/api/modules/evidence/types/evidence.ts. */
export const CONCLUSION_PARAGRAPH_MAX_LENGTH = 1200;

export interface EvidenceDraftSummary {
  draft_id: number;
  /** Exactly one of the two is set. */
  dispute_id: number | null;
  evidence_bank_case_id: number | null;
  status: EvidenceDraftStatus;
  order_type: EvidenceOrderType;
  dossier_schema_version: number | null;
  input_overrides: EvidenceInputOverrides | null;
  /** Compared against the conclusion's own hash to tell whether the text is behind the document. */
  facts_hash: string | null;
  /** Entered by the agent: Shopify's dispute API carries no ARN, so it cannot be collected. */
  manual_arn: string | null;
  no_contact_attested: boolean;
  /** As printed by the last generation. */
  generated_products: EvidenceGeneratedProduct[] | null;
  /** What the agent types into the store's own admin to find this case; the card carries both. */
  generated_order_name: string | null;
  /** The system the order was loaded from; null before the first run. */
  generated_order_source: EvidenceOrderSourceKind | null;
  generated_customer_email: string | null;
  conclusion: EvidenceConclusion | null;
  attention_items: EvidenceAttentionItem[] | null;
  error_message: string | null;
  /** Last attachment or manual-field change; later than generated_at means the PDF is stale. */
  changed_at: string | null;
  changed_labels: string[];
  generated_at: string | null;
  /** Of the published PDF; null on a draft printed before the figures were kept. */
  pdf_bytes: number | null;
  pdf_pages: number | null;
  /** The processor files cut from the published PDF; null before the package existed or for a processor that takes none. */
  package: EvidencePackage | null;
  /** The latest send of this dispute's evidence; null before one. */
  submission: DisputeEvidenceSubmission | null;
  /** A PayPal dispute's rounds and reopen; null for other processors and bank cases. */
  paypal: PayPalCaseView | null;
  created_at: string;
  updated_at: string;
}

export const EVIDENCE_ATTENTION_LABELS: Record<EvidenceAttentionCode, string> = {
  customer_comms_missing: 'Customer communication',
  tracking_unavailable: 'Tracking',
  tracking_pending: 'Tracking',
  disputed_parcels_untracked: 'Tracking',
  disputed_item_not_in_parcel: 'Tracking',
  policy_missing: 'Policy',
  cvc_avs_unavailable: 'Secured payment',
  static_asset_missing: 'Checkout screenshot',
  brand_logo_missing: 'Logo',
  order_unavailable: 'Order',
  admin_order_missing: 'Order in admin',
  payment_details_missing: 'Payment details',
  email_confirmation_missing: 'Order confirmation email',
  ip_location_capture: 'IP location',
  order_page_capture: 'Product page',
  order_status_capture: 'Order status page (customer view)',
  order_history_missing: 'Order history',
  ip_location_unavailable: 'IP location',
  ip_location_mismatch: 'IP location',
  ip_location_same_state: 'IP location',
  ip_location_anonymous: 'IP location',
  source_access_note: 'Data access',
  order_history_unavailable: 'Order history',
  enrolment_unavailable: 'Enrolment order',
  refund_pending: 'Refund',
  capture_failed: 'Screenshot',
  delivery_failed: 'Delivery',
  delivered_elsewhere: 'Delivery',
  parcel_never_moved: 'Delivery',
  disputed_items_unproven: 'Delivery',
  conclusion_missing: 'Conclusion',
  storefront_unresolved: 'Storefront',
  reason_unclassified: 'Reason',
  digital_proof_missing: 'Digital proof',
  account_page_missing: 'Account page',
};

export const EVIDENCE_SLOT_LABELS: Record<EvidenceSlot, string> = {
  admin_order: 'Order in admin',
  payment_details: 'Payment details',
  email_confirmation: 'Order confirmation email',
  customer_communication: 'Customer communication',
  order_history: 'Order history',
  checkout_process: 'Checkout process',
  order_page: 'Product page',
  ip_location: 'IP location',
  order_status: 'Order status page (customer view)',
  proof_of_delivery: 'Proof of delivery',
  proof_of_refund: 'Proof of refund',
  tracking_page: 'Carrier tracking page',
  product_photo: 'Product photo',
  account_activity: 'Account activity log',
  product_usage: 'Product used / results',
  email_activity: 'Email log',
};

export const EVIDENCE_STATUS_LABELS: Record<EvidenceDraftStatus, string> = {
  queued: 'Queued',
  collecting: 'Collecting data',
  rendering: 'Rendering',
  generated: 'Generated',
  failed: 'Failed',
  superseded: 'Superseded',
};

export const EVIDENCE_IN_PROGRESS_STATUSES: EvidenceDraftStatus[] = ['queued', 'collecting', 'rendering'];

/** What the preview pane narrates while a run is live. */
export const EVIDENCE_PROGRESS_STAGES: Partial<Record<EvidenceDraftStatus, { title: string; subtitle: string }>> = {
  queued: {
    title: 'Waiting for a worker',
    subtitle: 'The generation starts in a moment',
  },
  collecting: {
    title: 'Collecting order data',
    subtitle: 'Order, tracking, checkout IP and payment checks',
  },
  rendering: {
    title: 'Rendering the document',
    subtitle: 'Building the PDF from the collected record',
  },
};
/** Mirrors IN_FLIGHT_TIMEOUT_MS in apps/api/modules/evidence/evidenceDraftService.ts. */
export const EVIDENCE_IN_FLIGHT_TIMEOUT_MS = 15 * 60 * 1000;
/** The worker re-runs a draft the carrier had not synced for about 3 minutes after it published;
 * the run itself waits its turn behind other generations. */
export const EVIDENCE_TRACKING_FOLLOW_UP_MS = 15 * 60 * 1000;
/**
 * A generation finishes well under a minute, so a run this old is overdue and the page offers a way
 * out of the wait. The server still owns the run until EVIDENCE_IN_FLIGHT_TIMEOUT_MS; this only
 * decides when the page stops pretending the wait is normal.
 */
export const EVIDENCE_STALLED_AFTER_MS = 2 * 60 * 1000;

// Mirrors the upload rules in apps/api/routes/evidenceDrafts.ts.
export const IMAGE_UPLOAD_ACCEPT = 'image/png,image/jpeg';
const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;

export function imageUploadError(file: File, noun: string): string | null {
  if (!IMAGE_UPLOAD_ACCEPT.split(',').includes(file.type)) return `${noun} must be a PNG or JPEG image`;
  if (file.size > MAX_IMAGE_UPLOAD_BYTES) return `${noun} exceeds the 5 MB limit`;
  return null;
}

export const EVIDENCE_POLICY_SLOTS = ['cancellation', 'refund', 'shipping'] as const;
export type EvidencePolicySlot = typeof EVIDENCE_POLICY_SLOTS[number];

export interface EvidencePolicyText {
  body: string;
  url: string | null;
  /** When this copy was last written. Absent on policies seeded before it existed. */
  syncedAt?: string;
}

export const EVIDENCE_POLICY_SOURCES = ['manual', 'shopify'] as const;
export type EvidencePolicySource = typeof EVIDENCE_POLICY_SOURCES[number];

/** Mirrors EvidencePolicy in apps/api/modules/evidence/types/evidence.ts: two copies, `source` picks the one that prints. */
export interface EvidencePolicy {
  source?: EvidencePolicySource;
  manual?: EvidencePolicyText;
  shopify?: EvidencePolicyText;
}

// Mirrors STOREFRONT_ORIGINS in apps/api/modules/evidence/types/evidence.ts.
export const STOREFRONT_ORIGINS = ['shopify', 'order', 'manual'] as const;
export type StorefrontOrigin = typeof STOREFRONT_ORIGINS[number];

// Mirrors STOREFRONT_STATIC_ASSET_KINDS in apps/api/modules/evidence/types/evidence.ts.
export const STOREFRONT_STATIC_ASSET_KINDS = ['checkout', 'logo', 'account'] as const;
export type StorefrontStaticAssetKind = typeof STOREFRONT_STATIC_ASSET_KINDS[number];

// Mirrors StorefrontStaticAsset in apps/api/modules/evidence/types/evidence.ts: one asset per kind.
export interface StorefrontStaticAsset {
  key: string;
  kind: StorefrontStaticAssetKind;
  fileName?: string;
  sizeBytes?: number;
  uploadedAt?: string;
  contentType?: 'image/png' | 'image/jpeg';
}

/** Mirrors ReturnAddress in apps/api/schemas/paypalInquirySchemas.ts: PayPal's address fields. */
export interface ReturnAddress {
  address_line_1: string;
  admin_area_2: string;
  admin_area_1: string;
  postal_code: string;
  country_code: string;
}

// Mirrors Storefront in apps/api/modules/evidence/types/evidence.ts.
export interface Storefront {
  storefront_id: number;
  company_id: number;
  /** Registrable domain, lowercase; a Shopify-backed row starts with its myshopify host. */
  domain: string | null;
  name: string;
  shopify_account_id: number | null;
  origin: StorefrontOrigin;
  static_assets: StorefrontStaticAsset[] | null;
  policies: Partial<Record<EvidencePolicySlot, EvidencePolicy>> | null;
  /** Shopify order tags the store marks a subscription renewal with, beyond the built-in patterns. */
  renewal_order_tags: string[] | null;
  /** Agent-typed lines printed as bullets above the checkout screenshots. */
  checkout_notes: string[] | null;
  /** Sent to PayPal with the evidence when a not-as-described case asks the buyer to return the item. */
  return_address: ReturnAddress | null;
  /** Sells digital products: its evidence uses the digital template. */
  digital: boolean;
  /** The address the storefront answers on; prints when the store's own is unknown. */
  contact_email: string | null;
  created_at: string;
  updated_at: string;
}

// Mirrors StorefrontCompleteness in apps/api/modules/evidence/types/evidence.ts.
export interface StorefrontCompleteness {
  logo: boolean;
  checkout: boolean;
  /** Required slots with a printable text, out of requiredPolicySlots(storefront).length. */
  policies: number;
  contactEmail: boolean;
}

// Mirrors StorefrontSummary in apps/api/modules/evidence/types/evidence.ts.
export interface StorefrontSummary extends Storefront {
  completeness: StorefrontCompleteness;
}

export const EVIDENCE_POLICY_LABELS: Record<EvidencePolicySlot, string> = {
  cancellation: 'Subscription & cancellation policy',
  refund: 'Refund policy',
  shipping: 'Shipping policy',
};
